import { Check, Copy, ExternalLink, MoreVertical, Share2, Store, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { links, profile, socialLinks, type LinkItem, type SocialLink } from "../content/profile";
import { trackLinkClick, trackPageView, trackShareClick } from "../lib/analyticsClient";

function InstagramGlyph({ size = 32 }: { size?: number }) {
  return (
    <svg aria-hidden="true" fill="none" height={size} viewBox="0 0 24 24" width={size}>
      <rect height="17" rx="5" stroke="currentColor" strokeWidth="1.9" width="17" x="3.5" y="3.5" />
      <circle cx="12" cy="12" r="4.1" stroke="currentColor" strokeWidth="1.9" />
      <circle cx="17" cy="7" fill="currentColor" r="1.25" />
    </svg>
  );
}

function TikTokGlyph({ size = 32 }: { size?: number }) {
  return (
    <svg aria-hidden="true" fill="currentColor" height={size} viewBox="0 0 24 24" width={size}>
      <path d="M15.8 3c.3 2 1.4 3.4 3.2 4.2.6.3 1.2.4 2 .5v3.5a8.3 8.3 0 0 1-4.9-1.5v5.9c0 3.4-2.7 5.9-6.1 5.9a5.7 5.7 0 0 1-5.9-5.7c0-3.6 3.2-6.3 6.8-5.7v3.7c-1.6-.5-3.1.6-3.1 2.1 0 1.2 1 2.1 2.2 2.1 1.4 0 2.4-1.1 2.4-2.7V3h3.4Z" />
    </svg>
  );
}

function ShopeeGlyph({ size = 32 }: { size?: number }) {
  return (
    <svg aria-hidden="true" fill="none" height={size} viewBox="0 0 24 24" width={size}>
      <path
        d="M5.8 8.2h12.4l.7 11a1.8 1.8 0 0 1-1.8 1.9H6.9a1.8 1.8 0 0 1-1.8-1.9l.7-11Z"
        stroke="currentColor"
        strokeLinejoin="round"
        strokeWidth="1.8"
      />
      <path d="M8.2 8.1V6.9c0-2.5 1.7-4.3 3.8-4.3s3.8 1.8 3.8 4.3v1.2" stroke="currentColor" strokeLinecap="round" strokeWidth="1.8" />
      <text
        dominantBaseline="middle"
        fill="currentColor"
        fontFamily="Arial, Helvetica, sans-serif"
        fontSize="9.8"
        fontWeight="500"
        textAnchor="middle"
        x="12"
        y="14.8"
      >
        S
      </text>
    </svg>
  );
}

function linkIcon(type: SocialLink["type"] | LinkItem["slug"], size: number) {
  if (type === "instagram") return <InstagramGlyph size={size} />;
  if (type === "shopee") return <ShopeeGlyph size={size} />;
  if (type === "tiktok") return <TikTokGlyph size={size} />;

  return <Store aria-hidden="true" size={size} strokeWidth={2.1} />;
}

function SocialIcon({ social }: { social: SocialLink }) {
  const icon = linkIcon(social.type, 32);

  return (
    <a
      aria-label={social.label}
      className="profile-social-link"
      href={social.url}
      onClick={() => trackLinkClick(social.slug)}
      rel="noopener noreferrer"
      target="_blank"
      title={social.label}
    >
      {icon}
    </a>
  );
}

function LinkIcon({ link }: { link: LinkItem }) {
  return (
    <span className="profile-link-icon" aria-hidden="true">
      {linkIcon(link.slug, 24)}
    </span>
  );
}

function getShareUrl(link: LinkItem): string {
  return link.url;
}

async function copyText(value: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }

  const textarea = document.createElement("textarea");
  textarea.value = value;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand("copy");
  document.body.removeChild(textarea);
}

function ProfileLink({ link, onShare }: { link: LinkItem; onShare: (link: LinkItem) => void }) {
  return (
    <div className="profile-link-wrap" data-link-slug={link.slug}>
      <a
        className="profile-link"
        href={link.url}
        onClick={() => trackLinkClick(link.slug)}
        rel="noopener noreferrer"
        target="_blank"
      >
        <LinkIcon link={link} />
        <span className="profile-link-title">{link.title}</span>
      </a>
      <button
        aria-label={`Share ${link.title}`}
        className="profile-share-button"
        onClick={() => onShare(link)}
        title={`Share ${link.title}`}
        type="button"
      >
        <MoreVertical aria-hidden="true" size={18} strokeWidth={2.6} />
      </button>
    </div>
  );
}

function ShareSheet({ link, onClose }: { link: LinkItem; onClose: () => void }) {
  const [status, setStatus] = useState<"idle" | "copied" | "failed">("idle");
  const shareUrl = useMemo(() => getShareUrl(link), [link]);
  const canNativeShare = typeof navigator.share === "function";

  useEffect(() => {
    setStatus("idle");
  }, [link]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  async function handleCopy() {
    try {
      await copyText(shareUrl);
      setStatus("copied");
    } catch {
      setStatus("failed");
    }
  }

  async function handleNativeShare() {
    try {
      await navigator.share({
        title: `${profile.title} - ${link.title}`,
        text: link.description ?? link.title,
        url: shareUrl
      });
    } catch {
      setStatus("idle");
    }
  }

  return (
    <div className="share-sheet-backdrop" onClick={onClose} role="presentation">
      <section
        aria-labelledby="share-sheet-title"
        aria-modal="true"
        className="share-sheet"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
      >
        <button aria-label="Close share menu" className="share-sheet-close" onClick={onClose} type="button">
          <X aria-hidden="true" size={18} />
        </button>

        <div className="share-sheet-icon">{linkIcon(link.slug, 28)}</div>
        <h2 id="share-sheet-title">Share {link.title}</h2>
        <p>{shareUrl}</p>

        <div className="share-sheet-actions">
          <button className="share-action primary" onClick={handleCopy} type="button">
            {status === "copied" ? <Check aria-hidden="true" size={18} /> : <Copy aria-hidden="true" size={18} />}
            {status === "copied" ? "Copied" : "Copy link"}
          </button>
          {canNativeShare ? (
            <button className="share-action" onClick={handleNativeShare} type="button">
              <Share2 aria-hidden="true" size={18} />
              Share
            </button>
          ) : null}
          <a className="share-action" href={link.url} onClick={() => trackLinkClick(link.slug)} rel="noopener noreferrer" target="_blank">
            <ExternalLink aria-hidden="true" size={18} />
            Open
          </a>
        </div>

        {status === "failed" ? <span className="share-sheet-status">Copy failed</span> : null}
      </section>
    </div>
  );
}

export function PublicProfile() {
  const [shareLink, setShareLink] = useState<LinkItem | null>(null);

  useEffect(() => {
    trackPageView();
  }, []);

  function openShareSheet(link: LinkItem) {
    trackShareClick(link.slug);
    setShareLink(link);
  }

  return (
    <main className="profile-shell">
      <section className="profile-frame" aria-label={`${profile.title} links`}>
        <header className="profile-header">
          <img className="profile-avatar" src={profile.avatarPath} alt={`${profile.title} avatar`} />
          <h1>{profile.title}</h1>
          <p>{profile.description}</p>
          <nav className="profile-socials" aria-label="Social links">
            {socialLinks.map((social) => (
              <SocialIcon key={social.type} social={social} />
            ))}
          </nav>
        </header>

        <div className="profile-links" aria-label="Links">
          {links.map((link) => (
            <ProfileLink key={link.id} link={link} onShare={openShareSheet} />
          ))}
        </div>
      </section>
      {shareLink ? <ShareSheet link={shareLink} onClose={() => setShareLink(null)} /> : null}
    </main>
  );
}
